import orderBy from 'lodash/orderBy';
import React, { useMemo } from 'react';
import { ChartCard, generateChartColors, HorizontalBars, InfoTooltip } from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { useChartTranslations } from '@/hooks/useChartTranslations';

export const Index_FinancialReturnsFragment = gql(/* GraphQL */ `
  fragment Index_FinancialReturnsFragment on ForestryIndicators {
    targetFinancialReturns {
      items {
        count
        label
        pct
      }
      isDataSuppressed
    }
  }
`);

type FinancialReturnsProps = {
  className?: string;
  indicators: FragmentType<typeof Index_FinancialReturnsFragment>;
};

export function FinancialReturns({ indicators, className }: FinancialReturnsProps) {
  const { targetFinancialReturns } = useFragment(Index_FinancialReturnsFragment, indicators);

  const { title, subtitle, chartTooltip, getOptionLabel, getOptionTooltip } =
    useChartTranslations('financialReturns');

  const sortedItems = useMemo(() => {
    const colors = generateChartColors(targetFinancialReturns.items.length);
    const sorted = orderBy(targetFinancialReturns.items, ['pct'], ['desc']);
    return sorted.map((x, i) => ({ ...x, color: colors[i] }));
  }, [targetFinancialReturns.items]);

  return (
    <ChartCard className={className}>
      <ChartCard.Header>
        <ChartCard.Title>
          {title}
          {chartTooltip && <InfoTooltip className="ml-1">{chartTooltip}</InfoTooltip>}
        </ChartCard.Title>
        {subtitle && <ChartCard.Subtitle>{subtitle}</ChartCard.Subtitle>}
      </ChartCard.Header>
      <ChartCard.Body isDataSuppressed={targetFinancialReturns.isDataSuppressed}>
        <HorizontalBars min={0} max={100} showInlineDisplayValue={false}>
          {sortedItems.map((item, i) => {
            if (!item) return null;
            return (
              <HorizontalBars.Item
                key={i}
                label={getOptionLabel(item.label)}
                tooltipLabel={getOptionTooltip(item.label)}
                value={item.pct ?? null}
                displayValue={item.count?.toLocaleString() ?? ''}
                color={item.color}
              />
            );
          })}
        </HorizontalBars>
      </ChartCard.Body>
    </ChartCard>
  );
}
